import { Form, type FormProps, Modal, Typography, Flex } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'

import { type CouponBody, CouponService } from '@services/CouponService'
import { useNotifications } from '@store/notifications'

import type { Coupon, Product } from '../../../../types'

import { CouponForm } from './CouponForm'

type ModalProps = {
    coupon: Coupon
    isOpen: boolean
    onClose: () => void
    onSuccess: () => Promise<void>
    availableProducts: Product[]
}

export const EditCouponModal: React.FC<ModalProps> = ({ isOpen, coupon, onClose, onSuccess, availableProducts }) => {
    const { showNotification } = useNotifications()
    const [isLoading, setIsLoading] = useState(false)
    const [form] = Form.useForm()

    useEffect(() => {
        if (coupon) {
            form.setFieldsValue({
                code: coupon.code.trim().toLowerCase(),
                expiry: dayjs(coupon.expiry),
                productId: coupon.productId
            })
        }
    }, [coupon, form])

    const onFinish: FormProps<CouponBody>['onFinish'] = async values => {
        const body: CouponBody = {
            code: values.code.toLowerCase().trim(),
            expiry: values.expiry,
            productId: values.productId
        }

        try {
            setIsLoading(true)
            await CouponService.update(coupon.id, body)
            showNotification({ title: 'Coupon successully updated' })
            await onSuccess()
            onClose()
        } catch (err: any) {
            const message = err.response.data ? err.response.data : err.toString()
            showNotification({ title: 'Could not edit the coupon', type: 'error', description: message })
        } finally {
            setIsLoading(false)
        }
    }

    if (!coupon) {
        return null
    }

    return (
        <Modal open={isOpen} footer={[]} onCancel={onClose}>
            <Flex vertical gap="middle">
                <Typography.Title level={4}>Edit a coupon</Typography.Title>
                <CouponForm
                    form={form}
                    onFinish={onFinish}
                    availableProducts={availableProducts}
                    isLoading={isLoading}
                />
            </Flex>
        </Modal>
    )
}
